import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import Logo from './Logo';
import BRAND from '../config/brand';
import { useAuth } from '../contexts/AuthContext';

const NAV = [
  { to: '/pricing', label: 'Pricing' },
  { to: '/contact', label: 'Contact' },
];

export default function PublicLayout({ children }) {
  const [open, setOpen] = useState(false);
  const { user } = useAuth();
  const location = useLocation();

  return (
    <div className="min-h-screen bg-bg flex flex-col">
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-white/75 border-b border-line">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" data-testid="nav-logo"><Logo /></Link>
          <nav className="hidden md:flex items-center gap-1">
            {NAV.map((n) => (
              <Link key={n.to} to={n.to} data-testid={`nav-link-${n.label.toLowerCase()}`}
                className={`btn-ghost ${location.pathname === n.to ? 'text-ink' : ''}`}>{n.label}</Link>
            ))}
            {user ? (
              <Link to="/app" className="btn-primary !min-h-[44px] ml-2" data-testid="nav-open-app">Open app</Link>
            ) : (
              <>
                <Link to="/auth" className="btn-ghost" data-testid="nav-login">Log in</Link>
                <Link to="/auth?mode=signup" className="btn-primary !min-h-[44px] ml-2" data-testid="nav-cta-trial">
                  Start {BRAND.trialPrice} trial
                </Link>
              </>
            )}
          </nav>
          <button className="md:hidden btn-ghost !px-3" onClick={() => setOpen(!open)} data-testid="nav-mobile-toggle" aria-label="Menu">
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
        {open && (
          <div className="md:hidden border-t border-line bg-paper px-4 py-4 space-y-2 animate-fade-in">
            {NAV.map((n) => (
              <Link key={n.to} to={n.to} onClick={() => setOpen(false)} className="block py-3 text-sub font-semibold" data-testid={`mobile-nav-${n.label.toLowerCase()}`}>{n.label}</Link>
            ))}
            {user ? (
              <Link to="/app" onClick={() => setOpen(false)} className="btn-primary w-full" data-testid="mobile-nav-open-app">Open app</Link>
            ) : (
              <>
                <Link to="/auth" onClick={() => setOpen(false)} className="block py-3 text-sub font-semibold" data-testid="mobile-nav-login">Log in</Link>
                <Link to="/auth?mode=signup" onClick={() => setOpen(false)} className="btn-primary w-full" data-testid="mobile-nav-cta">Start {BRAND.trialPrice} trial</Link>
              </>
            )}
          </div>
        )}
      </header>

      <main className="flex-1">{children}</main>

      <footer className="border-t border-line mt-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12 grid grid-cols-1 sm:grid-cols-3 gap-8">
          <div className="space-y-3">
            <Logo size={28} />
            <p className="text-faint text-sm max-w-xs">{BRAND.tagline}. Built with respect, for India.</p>
          </div>
          <div className="text-sm space-y-2">
            <div className="overline mb-3">Product</div>
            <Link to="/pricing" className="block text-sub hover:text-ink transition-colors" data-testid="footer-pricing">Pricing</Link>
            <Link to="/contact" className="block text-sub hover:text-ink transition-colors" data-testid="footer-contact">Contact</Link>
          </div>
          <div className="text-sm space-y-2">
            <div className="overline mb-3">Legal</div>
            <Link to="/terms" className="block text-sub hover:text-ink transition-colors" data-testid="footer-terms">Terms of Service</Link>
            <Link to="/privacy" className="block text-sub hover:text-ink transition-colors" data-testid="footer-privacy">Privacy Policy</Link>
            <Link to="/refund" className="block text-sub hover:text-ink transition-colors" data-testid="footer-refund">Refund Policy</Link>
          </div>
        </div>
        <div className="border-t border-line/70">
          <p className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-5 text-xs text-faint">
            {BRAND.name} — practice today, charm tomorrow ✨ For users 18 and older. © {new Date().getFullYear()} {BRAND.name}.
          </p>
        </div>
      </footer>
    </div>
  );
}
